import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtMoney, fmtDate } from "@/lib/helpers";
import { FileCsv, FilePdf, Calendar as CalendarIcon, ChartBar, Users, CreditCard, EyeSlash } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";

import { devWarn } from "@/lib/devLog";
function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

function HideDemoToggle() {
  const [hide, setHide] = useState(false);
  const [editable, setEditable] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/workspace/hide-demo-flag");
        if (mounted) { setHide(!!data.hide); setEditable(!!data.editable); }
      } catch (e) {
        // Non-fatal: toggle just stays hidden if the pref endpoint is down.
        devWarn("[ReportsPage] hide-demo-flag load failed:", e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onChange = async (v) => {
    setSaving(true);
    try {
      await api.put("/workspace/hide-demo-flag", { hide: !!v });
      setHide(!!v);
      toast.success(v ? "Demo data hidden from reports" : "Demo data now visible in reports");
    } catch (e) {
      toast.error(formatApiError(e, "Failed to update preference"));
    } finally {
      setSaving(false);
    }
  };

  if (!editable) return null;
  return (
    <div className="flex items-center gap-2" data-testid="hide-demo-toggle-wrap">
      <EyeSlash size={14} weight="duotone" className="text-muted-foreground" />
      <label className="text-xs text-muted-foreground">Hide demo data</label>
      <Switch checked={hide} onCheckedChange={onChange} disabled={saving} data-testid="hide-demo-toggle" />
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState("revenue");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-1.5">Insights</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Attendance, revenue, and therapist performance — exportable as CSV or PDF.</p>
        </div>
        <HideDemoToggle />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max">
            <TabsTrigger value="revenue" data-testid="report-tab-revenue"><ChartBar size={14} className="mr-1.5" weight="duotone" />Revenue</TabsTrigger>
            <TabsTrigger value="by-method" data-testid="report-tab-by-method"><CreditCard size={14} className="mr-1.5" weight="duotone" />By Method</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="report-tab-attendance"><CalendarIcon size={14} className="mr-1.5" weight="duotone" />Attendance</TabsTrigger>
            <TabsTrigger value="therapist" data-testid="report-tab-therapist"><Users size={14} className="mr-1.5" weight="duotone" />Therapist Performance</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="revenue" className="mt-4"><RevenueReport /></TabsContent>
        <TabsContent value="by-method" className="mt-4"><RevenueByMethodReport /></TabsContent>
        <TabsContent value="attendance" className="mt-4"><AttendanceReport /></TabsContent>
        <TabsContent value="therapist" className="mt-4"><TherapistReport /></TabsContent>
      </Tabs>
    </div>
  );
}

function DateRange({ from, to, setFrom, setTo, onRun, onCsv, onPdf, extra = null }) {
  return (
    <div className="flex flex-wrap gap-2 items-end">
      <div className="space-y-1.5">
        <Label className="text-xs">From</Label>
        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-40" data-testid="from-date" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">To</Label>
        <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-40" data-testid="to-date" />
      </div>
      {extra}
      <Button onClick={onRun} data-testid="run-report">Run</Button>
      <Button variant="outline" onClick={onCsv} className="gap-1.5" data-testid="download-csv"><FileCsv size={14} weight="duotone" />CSV</Button>
      <Button variant="outline" onClick={onPdf} className="gap-1.5" data-testid="download-pdf"><FilePdf size={14} weight="duotone" />PDF</Button>
    </div>
  );
}

async function download(url) {
  try {
    const activeRole = localStorage.getItem("tf_active_role");
    const activeBranch = localStorage.getItem("tf_active_branch");
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        ...(activeRole ? { "X-Active-Role": activeRole } : {}),
        ...(activeBranch ? { "X-Active-Branch": activeBranch } : {}),
      },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : "report";
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(e.message || "Download failed"); }
}

function RevenueReport() {
  const { activeBranch } = useAuth();
  const branchId = activeBranch === "all" ? null : activeBranch;
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const q = new URLSearchParams({ from, to });
    if (branchId) q.set("branch_id", branchId);
    return q.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/revenue?${params()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [activeBranch]);

  return (
    <Card>
      <CardHeader className="flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base font-display tracking-tight">Revenue between {fmtDate(from)} — {fmtDate(to)}</CardTitle>
        <DateRange
          from={from} to={to} setFrom={setFrom} setTo={setTo}
          onRun={run}
          onCsv={() => download(`${API_BASE}/reports/revenue?${params()}&format=csv`)}
          onPdf={() => download(`${API_BASE}/reports/revenue?${params()}&format=pdf`)}
        />
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Kpi label="Billed" value={fmtMoney(data.totals?.billed || 0)} />
              <Kpi label="Collected" value={fmtMoney(data.totals?.collected || 0)} tone="success" />
              <Kpi label="Outstanding" value={fmtMoney(data.totals?.outstanding || 0)} tone="warn" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b border-border">
                  <tr>
                    <Th>Date</Th><Th>Invoice #</Th><Th>Student</Th><Th>Branch</Th>
                    <Th right>Total</Th><Th right>Paid</Th><Th right>Balance</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No invoices in this range.</td></tr>}
                  {data.rows.map((r, i) => (
                    <tr key={r.invoice_no || `row-${i}`} className="border-b border-border/60 hover:bg-muted/30">
                      <Td>{r.date}</Td><Td className="font-medium">{r.invoice_no}</Td><Td>{r.student}</Td><Td>{r.branch}</Td>
                      <Td right className="tabular-nums">{fmtMoney(r.total)}</Td>
                      <Td right className="tabular-nums text-emerald-700">{fmtMoney(r.paid)}</Td>
                      <Td right className="tabular-nums text-amber-700">{fmtMoney(r.balance)}</Td>
                      <Td><Badge className={r.status==="paid"?"bg-emerald-100 text-emerald-800":r.status==="partial"?"bg-amber-100 text-amber-800":"bg-red-100 text-red-800"}>{r.status}</Badge></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RevenueByMethodReport() {
  const { activeBranch } = useAuth();
  const branchId = activeBranch === "all" ? null : activeBranch;
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const q = new URLSearchParams({ from, to });
    if (branchId) q.set("branch_id", branchId);
    return q.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/revenue-by-method?${params()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [activeBranch]);

  const METHOD_STYLES = {
    stripe: "bg-indigo-100 text-indigo-800 border-indigo-200",
    upi: "bg-emerald-100 text-emerald-800 border-emerald-200",
    cash: "bg-amber-100 text-amber-800 border-amber-200",
    bank: "bg-sky-100 text-sky-800 border-sky-200",
    card: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    other: "bg-muted text-muted-foreground border-border",
  };

  const totalNet = data?.totals?.net || 0;

  return (
    <Card>
      <CardHeader className="flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base font-display tracking-tight">Revenue by payment method · {fmtDate(from)} — {fmtDate(to)}</CardTitle>
        <DateRange
          from={from} to={to} setFrom={setFrom} setTo={setTo}
          onRun={run}
          onCsv={() => download(`${API_BASE}/reports/revenue-by-method?${params()}&format=csv`)}
          onPdf={() => download(`${API_BASE}/reports/revenue-by-method?${params()}&format=pdf`)}
        />
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Kpi label="Collected" value={fmtMoney(data.totals?.collected || 0)} tone="success" />
              <Kpi label="Refunded" value={fmtMoney(data.totals?.refunded || 0)} tone="warn" />
              <Kpi label="Net revenue" value={fmtMoney(data.totals?.net || 0)} />
            </div>

            {data.rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No payments in this range.</div>
            ) : (
              <div className="space-y-2">
                {data.rows.map((r) => {
                  const pct = totalNet > 0 ? Math.round((r.net / totalNet) * 100) : 0;
                  return (
                    <div key={r.method} className="border border-border rounded-md p-3" data-testid={`method-row-${r.method}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge className={METHOD_STYLES[r.method] || METHOD_STYLES.other}>{r.label}</Badge>
                          <span className="text-xs text-muted-foreground">{r.count} payment{r.count !== 1 ? "s" : ""}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm tabular-nums">
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Collected</div>
                            <div className="text-emerald-700 font-medium">{fmtMoney(r.collected)}</div>
                          </div>
                          {r.refunded > 0 && (
                            <div className="text-right">
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Refunded</div>
                              <div className="text-rose-700 font-medium">−{fmtMoney(r.refunded)}</div>
                            </div>
                          )}
                          <div className="text-right min-w-24">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net</div>
                            <div className="font-display font-semibold">{fmtMoney(r.net)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ minWidth: pct > 0 ? "4px" : 0, width: `${Math.max(0, Math.min(100, pct))}%` }}
                        />
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground text-right tabular-nums">{pct}% of net</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceReport() {
  const { activeBranch } = useAuth();
  const branchId = activeBranch === "all" ? null : activeBranch;
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const q = new URLSearchParams({ from, to });
    if (branchId) q.set("branch_id", branchId);
    return q.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/attendance?${params()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [activeBranch]);

  return (
    <Card>
      <CardHeader className="flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base font-display tracking-tight">Attendance · {fmtDate(from)} — {fmtDate(to)}</CardTitle>
        <DateRange
          from={from} to={to} setFrom={setFrom} setTo={setTo}
          onRun={run}
          onCsv={() => download(`${API_BASE}/reports/attendance?${params()}&format=csv`)}
          onPdf={() => download(`${API_BASE}/reports/attendance?${params()}&format=pdf`)}
        />
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><Th>Date</Th><Th>Type</Th><Th>Name</Th><Th>Branch</Th><Th>Status</Th><Th>In</Th><Th>Out</Th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No attendance in this range.</td></tr>}
                {data.rows.map((r, i) => (
                  <tr key={`${r.date || "d"}-${r.subject_id || r.name || i}`} className="border-b border-border/60 hover:bg-muted/30">
                    <Td>{r.date}</Td>
                    <Td><Badge variant="outline" className="text-[10px]">{r.type}</Badge></Td>
                    <Td className="font-medium">{r.name}</Td>
                    <Td>{r.branch}</Td>
                    <Td>{r.status}</Td>
                    <Td>{(r.time_in || "").slice(11, 16)}</Td>
                    <Td>{(r.time_out || "").slice(11, 16)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TherapistReport() {
  const { activeBranch } = useAuth();
  const branchId = activeBranch === "all" ? null : activeBranch;
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const q = new URLSearchParams({ from, to });
    if (branchId) q.set("branch_id", branchId);
    return q.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/therapist-performance?${params()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [activeBranch]);

  return (
    <Card>
      <CardHeader className="flex-col md:flex-row md:items-center md:justify-between gap-3">
        <CardTitle className="text-base font-display tracking-tight">Therapist performance · {fmtDate(from)} — {fmtDate(to)}</CardTitle>
        <DateRange
          from={from} to={to} setFrom={setFrom} setTo={setTo}
          onRun={run}
          onCsv={() => download(`${API_BASE}/reports/therapist-performance?${params()}&format=csv`)}
          onPdf={() => download(`${API_BASE}/reports/therapist-performance?${params()}&format=pdf`)}
        />
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><Th>Therapist</Th><Th right>Sessions</Th><Th right>Completed</Th><Th right>Completion %</Th><Th right>Avg rating</Th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No sessions in this range.</td></tr>}
                {data.rows.map((r, i) => (
                  <tr key={r.therapist_id || r.therapist || `t-${i}`} className="border-b border-border/60 hover:bg-muted/30">
                    <Td className="font-medium">{r.therapist}</Td>
                    <Td right className="tabular-nums">{r.sessions}</Td>
                    <Td right className="tabular-nums">{r.completed}</Td>
                    <Td right className="tabular-nums">{r.completion_pct}%</Td>
                    <Td right className="tabular-nums">{r.avg_rating ? `${r.avg_rating} / 5` : "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, tone }) {
  const toneCls = tone === "success" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "";
  return (
    <div className="border border-border rounded-md p-4">
      <div className="overline">{label}</div>
      <div className={`font-display text-2xl font-bold tracking-tight mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Th({ children, right }) { return <th className={`py-2 px-2 font-medium text-xs uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`py-2 px-2 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
